// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import useTrackLink from "@store/Framework/GoogleAnalytics/hooks/useTrackLink";
import setChangeRequestOrder from "@store/Framework/Store/Greif/Pages/RequestOrderChange/Handler/SetChangeRequestOrder";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { ChangeRequestModel } from "@store/Library/Widgets/RequestOrderChange/RequestOrderChangePageContainer";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    orderState: getOrderState(state, state.pages.orderDetails.orderNumber),
    changeRequestOrderState: state.greif.pages.orderChange,
    orderHistoryLink: getPageLinkByPageType(state, "OrderHistoryPage"),
});

const mapDispatchToProps = {
    setChangeRequestOrder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ReviewRequestOrderChangePageContainerStyles {
    confirmationContainer?: InjectableCss;
    successInfosBox?: InfosBoxProps;
    noResultInfosBox?: InfosBoxProps;
    goBackButton?: ButtonPresentationProps;
}

const reviewRequestOrderChangePageContainerStyles: ReviewRequestOrderChangePageContainerStyles = {
    confirmationContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: ${pxToRem(32)};
        `,
    },
    successInfosBox: {
        type: "success",
    },
    noResultInfosBox: {
        type: "error",
    },
    goBackButton: {
        css: css`
            ${x => x.theme.button.primary.css}
            margin-top: ${pxToRem(16)};
        `,
    },
};

const styles = reviewRequestOrderChangePageContainerStyles;

const NoResultsWrapper = () => {
    return (
        <StyledWrapper {...styles.confirmationContainer}>
            <InfosBox {...styles.noResultInfosBox}>
                {translate("There is no change request to review for this order.")}
            </InfosBox>
        </StyledWrapper>
    );
};

const ReviewRequestOrderChangePageContainer = ({
    id,
    history,
    orderHistoryLink,
    orderState,
    changeRequestOrderState,
    setChangeRequestOrder,
}: Props) => {
    const order = orderState.value;
    const { changeRequestOrder, requestSubmitted, failedToSubmitRequest } = changeRequestOrderState;
    const toasterContext = useContext(ToasterContext);
    const { isEditing } = useContext(ShellContext);
    const trackLink = useTrackLink();

    const [hasOrderData, setHasOrderData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const currentOrderRequestId = `orderChange-${order?.id}`;
    const changeRequestOrderJson = sessionStorage.getItem(currentOrderRequestId);

    if (isEditing && !order) {
        return <Zone contentId={id} zoneName="Container" />;
    }

    if (!order) {
        return null;
    }

    const goToOrderDetails = () => {
        if (orderHistoryLink) {
            history.push(orderHistoryLink.url);
        }
    };

    if (!changeRequestOrder.orderId && !changeRequestOrderJson) {
        return (
            <>
                <NoResultsWrapper />
                <Button onClick={goToOrderDetails} {...styles.goBackButton}>
                    {translate("Go back to Order History")}
                </Button>
            </>
        );
    }

    useEffect(() => {
        if (requestSubmitted) {
            trackLink({ page_type: "Review Order Change Request", link_clicked: "Send Change Request - Sent" });
        }
    }, [requestSubmitted]);

    useEffect(() => {
        if (failedToSubmitRequest) {
            toasterContext.addToast({
                body: siteMessage("ReviewOrderChange_ChangeError"),
                messageType: "warning",
            });
            trackLink({ page_type: "Review Order Change Request", link_clicked: "Send Change Request - Failed" });
        }
    }, [failedToSubmitRequest]);

    useEffect(() => {
        if (!changeRequestOrderJson) {
            setIsLoading(false);
        }

        if (changeRequestOrderJson) {
            const changeRequestOrderObj: ChangeRequestModel = JSON.parse(changeRequestOrderJson);
            setChangeRequestOrder({ changeRequestOrder: changeRequestOrderObj });
            setHasOrderData(true);
            setIsLoading(false);
        }
    }, []);

    if (requestSubmitted) {
        return (
            <StyledWrapper {...styles.confirmationContainer}>
                <InfosBox {...styles.successInfosBox}>{siteMessage("ReviewOrderChange_ChangeSuccess")}</InfosBox>
                <Button onClick={goToOrderDetails}>{translate("Go back to Order History")}</Button>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay loading={isLoading}>
            {hasOrderData ? <Zone contentId={id} zoneName="Container" /> : <NoResultsWrapper />}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ReviewRequestOrderChangePageContainer)),
    definition: {
        allowedContexts: ["ReviewOrderChangeRequestPage" as any],
        group: constants.widgetGroup.reviewOrderChangeRequest,
    },
};

export default widgetModule;
